/**
 * Unified API Client for NumixSheet / RowVault.
 * Resolves API base URL dynamically from environment variables (VITE_API_BASE_URL)
 * for cross-domain / CDN deployments or falls back to relative paths for same-origin hosting.
 */

import { getActiveUser } from './authStore.js';

export function getApiBaseUrl(): string {
  const envUrl = (import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL;
  if (envUrl && typeof envUrl === 'string') {
    return envUrl.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && (window as unknown as { __ENV__?: { VITE_API_BASE_URL?: string } }).__ENV__?.VITE_API_BASE_URL) {
    return (window as unknown as { __ENV__?: { VITE_API_BASE_URL?: string } }).__ENV__!.VITE_API_BASE_URL!.replace(/\/+$/, '');
  }
  return '';
}

export function apiUrl(endpoint: string): string {
  const base = getApiBaseUrl();
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${base}${cleanEndpoint}`;
}

export async function apiFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const url = apiUrl(endpoint);
  const token = getActiveUser()?.token;
  const defaultInit: RequestInit = {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
  };
  return fetch(url, defaultInit);
}

/** Sends a large file as a raw stream; it is never base64 encoded or JSON copied. */
export async function uploadColumnarFile(file: File, tableName?: string, signal?: AbortSignal): Promise<Response> {
  const token = getActiveUser()?.token;
  return fetch(apiUrl('/api/engine/import'), {
    method: 'POST',
    credentials: 'include',
    body: file,
    ...(signal ? { signal } : {}),
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': file.name,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tableName ? { 'X-Table-Name': tableName } : {}),
    },
  });
}

export async function runServerColumnarQuery(sql: string, params: unknown[] = [], maxRows = 10_000): Promise<Response> {
  return apiFetch('/api/engine/query', {
    method: 'POST',
    body: JSON.stringify({ sql, params, maxRows }),
  });
}

export async function fetchServerViewport(table: string, offset = 0, limit = 200, columns = '*'): Promise<Response> {
  return apiFetch('/api/engine/viewport', {
    method: 'POST',
    body: JSON.stringify({ table, offset, limit, columns }),
  });
}

export async function deleteServerRows(table: string, start: number, end: number): Promise<Response> {
  return apiFetch('/api/engine/delete-rows', {
    method: 'POST',
    body: JSON.stringify({ table, start, end }),
  });
}

export async function deleteServerColumns(table: string, start: number, count: number): Promise<Response> {
  return apiFetch('/api/engine/delete-cols', {
    method: 'POST',
    body: JSON.stringify({ table, start, count }),
  });
}

export async function sortServerTable(table: string, keys: { col: number; direction: 'asc' | 'desc' }[]): Promise<Response> {
  return apiFetch('/api/engine/sort', {
    method: 'POST',
    body: JSON.stringify({ table, keys }),
  });
}

export async function fetchServerFilteredViewport(table: string, column: number, condition: unknown, offset = 0, limit = 200): Promise<Response> {
  return apiFetch('/api/engine/filter-viewport', {
    method: 'POST',
    body: JSON.stringify({ table, column, condition, offset, limit }),
  });
}
